import _ from 'lodash';
import Immutable from 'seamless-immutable';

import RefData from 'ref-data';

import { assetRefService } from '../../common/Services';

const getMutable = (data, opts = {}) => (Immutable.isImmutable(data) ? data.asMutable(opts) : data);

/**
 * Page
 * @typedef {Object} Page
 *
 * @property {string} byline - a description of what the page is used for
 * @property {RefData} classRef - the page classRef [class.displayobject.*]
 * @property {string} heading -the page headline, sort of like an alternative page name
 * @property {number} id - the page id
 * @property {string} lastUpdated - datetime audit string for last time record was updated
 * @property {Object} layoutConfig - the JSON that describes the page layout
 * @property {Object} modifiedBy - email of the person to last modify the page
 * @property {Object} phaseInfo the phase index, label and whether the page should be reorderable within that phase
 * @property {string} state - this is in effect a string id of the page that helps ordering work with display
 * @property {string} thumbnail - icon thumbnail
 * @property {RefData} typeRef - the page typeRef [type.displayobject.*]
 * @property {string} status - the page status
 */

const defaultLayout = {
  modules:    [],
  pageLayout: {
    sections: [
      {
        type: 'content',
        rows: [{ columns: [{ modules: [] }], type: '1col_full' }],
        data: { linkText: '', style: '' },
      },
    ],
  },
};

/**
 * Create a PageModel from provided data
 */
function create(page = {}) {
  const result = _.clone(page);

  result.typeRef = _.isString(page.typeRef) ? RefData.Model.hydrate(page.typeRef) : page.typeRef;
  result.classRef = _.isString(page.classRef) ? RefData.Model.hydrate(page.classRef) : page.classRef;

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend page object into a frontend Page Model
 * @param {Object} page - backend formatted page
 * @returns {Page}
 */
function deserialize(page) {
  const result = _.pick(page, _keys);

  result.thumbnail = assetRefService.buildAssetRef(
    'static',
    'image',
    'auto',
    'c/img/experience-workspace/displayobject-landing-slide.svg'
  );

  result.layoutConfig = page.layoutConfig ? JSON.parse(page.layoutConfig) : Object.assign({}, defaultLayout);

  return create(result);
}

/**
 * Transform a PageModel into a backend page object
 * @returns {Object}
 */
function serialize(page) {
  const result = getMutable(page, { deep: true });

  if (result.layoutConfig) result.layoutConfig = JSON.stringify(result.layoutConfig);
  if (result.typeRef) result.typeRef = page.typeRef.id;
  if (result.classRef) result.classRef = page.classRef.id;
  return _.pick(result, _keys);
}

/** The PageModel prototype */
const model = {
  get assetRef() {
    return assetRefService.buildAssetRef('static', 'image', 'auto', `c/img/icons/page/page-${this.classRef.code}.svg`);
  },
  get headline() {
    return this.classRef.headline;
  },
  get byline() {
    return this.classRef.byline;
  },
  get shortByline() {
    return this.classRef.byline ? this.classRef.byline.split('.')[0] : '';
  },
  get canDelete() {
    return this.isContentPage || this.isCountdownPage || this.isCurtainPage;
  },
  get isContentPage() {
    return this.classRef.id === RefData.Enum.CLASS.DISPLAYOBJECT.CONTENT;
  },
  get isCountdownPage() {
    return this.classRef.id === RefData.Enum.CLASS.DISPLAYOBJECT.COUNTDOWN;
  },
  get isCurtainPage() {
    return this.classRef.id === RefData.Enum.CLASS.DISPLAYOBJECT.CURTAIN;
  },
  get isConfirmationPage() {
    return this.classRef.id === RefData.Enum.CLASS.DISPLAYOBJECT.CONFIRMATION;
  },
};

/**
 * An array of keys on the API Theme object that should be shared between the API requests and responses
 */
const _keys = [
  'classRef',
  'heading',
  'id',
  'lastUpdated',
  'layoutConfig',
  'name',
  'modifiedBy',
  'phaseInfo',
  'state',
  'thumbnail',
  'typeRef',
  'status',
];

export default {
  create,
  deserialize,
  serialize,
};
